import Button from '../../components/button/button'
import BaseComponent from '../../components/base-component/base-component'
import { bodyElement } from '../../components/common/body-element'
import CartAPI from '../../api/cart-api'
import { fetchConfig } from '../../utils/fetch-config'
import { serializeForm } from '../../utils/serialize-form'
import { delay } from '../../utils/utils'
import CartDrawer from '../cart/cart-drawer'
import Cart from '../cart/cart'
import CartNotificationPopup from '../common/cart-notification-popup'
import { Nullable } from '../../types/common'

class ProductForm extends BaseComponent {
  cartDrawer: Nullable<CartDrawer>
  cart: Nullable<Cart>
  cartNotificationPopup: Nullable<CartNotificationPopup>
  formSelector: string
  variantInputSelector: string
  form: Nullable<HTMLElement>
  variantInput: Nullable<HTMLElement>
  cartApi: CartAPI

  constructor() {
    super()

    this.cartDrawer = document.querySelector(`#CartDrawer`)
    this.cart = document.querySelector('cart-page')
    this.cartNotificationPopup = document.querySelector(
      '#CartNotificationPopup',
    )

    this.formSelector = 'form'
    this.variantInputSelector = `[data-product-form-variant]`

    this.form = this.querySelector(this.formSelector)
    this.quickView = document.getElementById('Quick-view')

    if (this.form) {
      this.variantInput = this.form.querySelector(this.variantInputSelector)
    }

    this.cartApi = new CartAPI()
  }

  connectedCallback(): void {
    this.form.addEventListener('submit', this.handleFormSubmit)
  }

  disconnectedCallback(): void {
    this.form.removeEventListener('submit', this.handleFormSubmit)
  }

  handleFormSubmit = (event: SubmitEvent): void => {
    event.preventDefault()

    const config = this.getQueryConfig()

    this.setDisable(true)
    this.setLoading(true)

    this.cartApi
      .add(config)
      .then(state => {
        const parsedState = JSON.parse(state)

        this.setDisable(false)
        this.setLoading(false)

        if (parsedState.description) {
          if (!bodyElement.notification) {
            return
          }

          const errorMessage =
            typeof parsedState.description === 'object'
              ? this.getErrorMessageFromObject(parsedState.description)
              : parsedState.description

          bodyElement.notification.show(errorMessage, 'warning')

          return
        }

        if (this.cart) {
          this.updateCart(parsedState)
        } else if (this.cartDrawer) {
          this.updateCartDrawerByParsedState(parsedState)
        } else {
          this.updatePopupByParsedState(parsedState)
        }
      })
      .catch(() => {
        if (!bodyElement.notification) {
          return
        }

        bodyElement.notification.show(
          'Error in product form component',
          'warning',
        )
      })
  }

  getErrorMessageFromObject(errorObject: object) {
    return Object.keys(errorObject)
      .map(key => {
        return `${key}: ${errorObject[key]}`
      })
      .join(' , ')
  }

  updateCart(parsedState): void {
    if (!this.cart) {
      return
    }

    this.cart.updateEmptyStatus(false)

    this.showCallbackAfterCloseDialogWindow(() => {
      this.cart.purchaseHandler(parsedState)
    })
  }

  updateCartDrawerByParsedState(parsedState) {
    if (!this.cartDrawer) {
      return
    }

    this.cartDrawer.updateEmptyStatus()

    const openCartDrawerCallback = () => {
      if (!this.cartDrawer) {
        return
      }

      this.cartDrawer.purchaseHandler(parsedState)
    }

    this.showCallbackAfterCloseDialogWindow(openCartDrawerCallback)
  }

  updatePopupByParsedState(parsedState) {
    const openPopupCallback = () => {
      if (!this.cartNotificationPopup) {
        return
      }

      this.cartNotificationPopup.showProductFromParsedState(
        parsedState,
        parsedState.id,
      )
    }

    this.showCallbackAfterCloseDialogWindow(openPopupCallback)
  }

  showCallbackAfterCloseDialogWindow(callback) {
    return delay(0).then(() => {
      if (this.quickView.isOpen) {
        this.quickView.hide().then(callback)
      } else {
        callback()
      }
    })
  }

  getFormData() {
    return JSON.parse(serializeForm(this.form))
  }

  getQueryConfig() {
    const config: any = fetchConfig('javascript')
    const cartDrawerSectionId =
      this.cartDrawer?.dataset.sectionId || 'cart-drawer'
    const cartSectionId = this.cart?.dataset.sectionId || 'cart'
    const sections = [
      cartDrawerSectionId,
      cartSectionId,
      'cart-notification-popup-content',
    ]

    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    config.body = JSON.stringify({
      ...this.getFormData(),
      sections,
      sections_url: window.location.pathname,
    })

    return config
  }

  setLoading(isLoading: boolean): void {
    const submitButtons = this.querySelectorAll('button[type="submit"]')

    submitButtons.forEach(submitButton => {
      const submitButtonComponent = new Button(submitButton)

      submitButtonComponent.setLoading(isLoading)
    })
  }

  setDisable(isDisable: boolean): void {
    const submitButtons = this.querySelectorAll('button[type="submit"]')

    submitButtons.forEach(submitButton => {
      const submitButtonComponent = new Button(submitButton)

      submitButtonComponent.setDisable(isDisable)
    })
  }

  setButtonText(text: string): void {
    const submitButtons = this.querySelectorAll('button[type="submit"]')

    submitButtons.forEach(submitButton => {
      const submitButtonComponent = new Button(submitButton)

      submitButtonComponent.setText(text)
    })
  }
}

export default ProductForm
